import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';

const { FiX, FiClock, FiDollarSign, FiUser, FiCalendar, FiCheck } = FiIcons;

const TimeInvoiceModal = ({ isOpen, onClose, onCreateInvoice }) => {
  const { tasks, contacts } = useData();
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [invoiceData, setInvoiceData] = useState({
    client: '',
    hourlyRate: 75,
    description: 'Time-based services',
    dueDate: ''
  });
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Filter tasks with tracked time
  const eligibleTasks = tasks.filter(task => {
    const hasTime = task.timeSpent > 0;
    const notInvoiced = !task.invoiced;
    const taskDate = task.createdAt || new Date();
    const isInDateRange = taskDate >= new Date(dateRange.startDate) && taskDate <= new Date(dateRange.endDate);
    
    return hasTime && notInvoiced && isInDateRange;
  });

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatDecimalTime = (seconds) => {
    return (seconds / 3600).toFixed(2);
  };

  const toggleTaskSelection = (taskId) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    setSelectedTasks(newSelected);
  };

  const selectAllTasks = () => {
    if (selectedTasks.size === eligibleTasks.length) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(eligibleTasks.map(task => task.id)));
    }
  };

  const calculateTotals = () => {
    const selectedTaskObjects = eligibleTasks.filter(task => selectedTasks.has(task.id));
    const totalSeconds = selectedTaskObjects.reduce((sum, task) => sum + task.timeSpent, 0);
    const totalHours = totalSeconds / 3600;
    const totalAmount = totalHours * invoiceData.hourlyRate;
    
    return {
      totalSeconds,
      totalHours,
      totalAmount,
      taskCount: selectedTaskObjects.length
    };
  };

  const handleCreateInvoice = () => {
    const selectedTaskObjects = eligibleTasks.filter(task => selectedTasks.has(task.id));
    const totals = calculateTotals();
    
    if (selectedTaskObjects.length === 0 || !invoiceData.client) {
      return;
    }

    // Group tasks by project for better organization
    const tasksByProject = selectedTaskObjects.reduce((acc, task) => {
      const project = task.project || 'General Tasks';
      if (!acc[project]) {
        acc[project] = [];
      }
      acc[project].push(task);
      return acc;
    }, {});

    // Create invoice items
    const items = Object.entries(tasksByProject).map(([project, projectTasks]) => {
      const projectTime = projectTasks.reduce((sum, task) => sum + task.timeSpent, 0);
      const projectHours = projectTime / 3600;
      
      return {
        description: `${project} - Time tracking (${projectTasks.length} tasks, ${formatTime(projectTime)})`,
        quantity: parseFloat(formatDecimalTime(projectTime)),
        rate: invoiceData.hourlyRate,
        amount: projectHours * invoiceData.hourlyRate,
        tasks: projectTasks.map(task => ({
          title: task.title,
          time: formatTime(task.timeSpent),
          hours: formatDecimalTime(task.timeSpent)
        }))
      };
    });

    const invoice = {
      client: invoiceData.client,
      description: invoiceData.description,
      dueDate: invoiceData.dueDate ? new Date(invoiceData.dueDate) : null,
      items,
      notes: `Invoice generated from time tracking data.\nDate range: ${dateRange.startDate} to ${dateRange.endDate}\nTotal tracked time: ${formatTime(totals.totalSeconds)} (${totals.totalHours.toFixed(2)} hours)\nHourly rate: $${invoiceData.hourlyRate}`,
      timeTracked: true,
      trackedTasks: selectedTaskObjects.map(task => task.id)
    };

    onCreateInvoice(invoice, Array.from(selectedTasks));
    onClose();
  };

  const totals = calculateTotals();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">
              Create Invoice from Time Tracking
            </h2>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <SafeIcon icon={FiX} />
            </motion.button>
          </div>

          <div className="p-6 space-y-6">
            {/* Invoice Settings */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Invoice Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiUser} className="inline mr-2" />
                    Client
                  </label>
                  <select
                    value={invoiceData.client}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, client: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Client</option>
                    {contacts.map(contact => (
                      <option key={contact.id} value={contact.name}>{contact.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiDollarSign} className="inline mr-2" />
                    Hourly Rate ($)
                  </label>
                  <input
                    type="number"
                    value={invoiceData.hourlyRate}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, hourlyRate: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiCalendar} className="inline mr-2" />
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={invoiceData.dueDate}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, dueDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={invoiceData.description}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Service description"
                  />
                </div>
              </div>
            </div>

            {/* Date Range Filter */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Time Period</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Task Selection */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  <SafeIcon icon={FiClock} className="inline mr-2" />
                  Select Tasks to Invoice ({eligibleTasks.length} available)
                </h3>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={selectAllTasks}
                  className="bg-blue-100 text-blue-700 px-3 py-1 rounded-lg text-sm hover:bg-blue-200 transition-colors"
                >
                  {selectedTasks.size === eligibleTasks.length ? 'Deselect All' : 'Select All'}
                </motion.button>
              </div>

              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        <input
                          type="checkbox"
                          checked={selectedTasks.size === eligibleTasks.length && eligibleTasks.length > 0}
                          onChange={selectAllTasks}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Task</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Project</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Assignee</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Time</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Hours</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligibleTasks.map((task) => (
                      <tr 
                        key={task.id} 
                        className={`border-b border-gray-100 hover:bg-gray-50 ${
                          selectedTasks.has(task.id) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <td className="py-3 px-4">
                          <input
                            type="checkbox"
                            checked={selectedTasks.has(task.id)}
                            onChange={() => toggleTaskSelection(task.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="py-3 px-4 font-medium text-gray-800">{task.title}</td>
                        <td className="py-3 px-4 text-gray-600">{task.project || 'Unassigned'}</td>
                        <td className="py-3 px-4 text-gray-600">{task.assignee || 'Unassigned'}</td>
                        <td className="py-3 px-4 text-right font-mono">{formatTime(task.timeSpent)}</td>
                        <td className="py-3 px-4 text-right font-mono">{formatDecimalTime(task.timeSpent)}</td>
                        <td className="py-3 px-4 text-right font-semibold text-green-600">
                          ${((task.timeSpent / 3600) * invoiceData.hourlyRate).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {eligibleTasks.length === 0 && (
                  <div className="text-center py-8">
                    <SafeIcon icon={FiClock} className="text-4xl text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No eligible tasks found for the selected date range.</p>
                    <p className="text-gray-400 text-sm">Tasks must have tracked time and not be previously invoiced.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Summary */}
            {selectedTasks.size > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-3">Invoice Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-blue-600 font-medium">Selected Tasks</div>
                    <div className="text-lg font-bold text-blue-800">{totals.taskCount}</div>
                  </div>
                  <div>
                    <div className="text-blue-600 font-medium">Total Time</div>
                    <div className="text-lg font-bold text-blue-800">{formatTime(totals.totalSeconds)}</div>
                  </div>
                  <div>
                    <div className="text-blue-600 font-medium">Billable Hours</div>
                    <div className="text-lg font-bold text-blue-800">{totals.totalHours.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-blue-600 font-medium">Total Amount</div>
                    <div className="text-lg font-bold text-blue-800">${totals.totalAmount.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-3 pt-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleCreateInvoice}
                disabled={selectedTasks.size === 0 || !invoiceData.client}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiCheck} />
                <span>Create Invoice (${totals.totalAmount.toFixed(2)})</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TimeInvoiceModal;